import React from 'react';
import { Link } from 'react-router-dom';

export default function StoebearbejdeContentSection() {
  const concreteRequirements = [
    {
      title: 'Korrekt opbygget underlag',
      desc: 'Et bæredygtigt sand- og gruslag samt komprimering, der forhindrer fremtidige sætningsskader.',
      icon: 'fa-duotone fa-layer-group',
      badge: 'Underlag'
    },
    {
      title: 'Præcise mål & højdekoter',
      desc: 'Nøjagtig afsætning og laserstyret afretning, så fundamentet passer millimeterpræcist til tegningerne.',
      icon: 'fa-duotone fa-ruler-combined',
      badge: 'Præcision'
    },
    {
      title: 'Dimensioneret til belastning',
      desc: 'Armering, betonstyrke og støbedybde tilpasset bygningens vægt og jordbundens bæreevne.',
      icon: 'fa-duotone fa-cubes-stacked',
      badge: 'Bæreevne'
    },
    {
      title: 'Helhedsløsning fra rå jord til beton',
      desc: 'Sammenhæng mellem udgravning, dræn, kloakføring og færdigstøbt sokkel uden fordyrende overgange.',
      icon: 'fa-duotone fa-trowel-bricks',
      badge: 'Totalproces'
    }
  ];

  const processSteps = [
    {
      step: '01',
      phase: 'Trin 1',
      title: 'Gennemgang & Forberedelse',
      text: 'Opgaven gennemgås, så mål, niveauer, jordforhold og adgang er afklaret, inden maskiner og materialer sættes i arbejde.',
      icon: 'fa-duotone fa-clipboard-check'
    },
    {
      step: '02',
      phase: 'Trin 2',
      title: 'Udgravning & Klargøring',
      text: 'Udgravning og klargøring udføres med fokus på et jævnt og stabilt underlag, der passer til projektets videre forløb.',
      icon: 'fa-duotone fa-shovel'
    },
    {
      step: '03',
      phase: 'Trin 3',
      title: 'Præcis Støbning',
      text: 'Støbningen planlægges og udføres præcist, så fundamentet danner et sikkert udgangspunkt for de næste håndværkere.',
      icon: 'fa-duotone fa-trowel-bricks'
    }
  ];

  const priceFactors = [
    {
      title: 'Mål & Mængde af Beton',
      desc: 'Projektets mål og den nødvendige mængde beton har betydning for både materialer, arbejdstid og planlægning.',
      icon: 'fa-solid fa-ruler-triangle'
    },
    {
      title: 'Jordbund & Udgravningsdybde',
      desc: 'Jordbund, udgravningsdybde og behovet for bortkørsel påvirker, hvor meget klargøring der skal udføres.',
      icon: 'fa-solid fa-layer-group'
    },
    {
      title: 'Adgangsforhold & Koordinering',
      desc: 'Adgangsforhold og koordinering med andre fag kan ændre den praktiske udførelse og det samlede omfang.',
      icon: 'fa-solid fa-truck-ramp-couch'
    }
  ];

  const serviceTowns = [
    { name: 'Sommersted', isBase: true },
    { name: 'Vojens', isBase: false },
    { name: 'Haderslev', isBase: false },
    { name: 'Kolding', isBase: false },
    { name: 'Vejen', isBase: false },
    { name: 'Gram', isBase: false },
    { name: 'Jels', isBase: false }
  ];

  return (
    <div className="space-y-12">
      {/* Intro Banner */}
      <div className="rounded-xl border border-brand-orange-500/20 bg-gradient-to-br from-brand-navy-900 to-brand-navy-950 p-6 md:p-8 text-white shadow-lg relative overflow-hidden">
        <div className="absolute top-0 right-0 -mt-6 -mr-6 w-36 h-36 bg-brand-orange-500/10 rounded-full blur-2xl pointer-events-none" />
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative z-10">
          <div className="space-y-3 max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-orange-500/20 border border-brand-orange-400/30 text-brand-orange-300 text-xs font-bold uppercase tracking-wider">
              <i className="fa-solid fa-certificate" /> Mere end 20 års erfaring
            </div>
            <h2 className="font-display text-xl sm:text-2xl md:text-3xl font-bold tracking-tight text-white leading-snug">
              Støbearbejde i Sommersted med et solidt resultat
            </h2>
            <p className="text-brand-navy-200 text-base md:text-lg leading-relaxed">
              Hos Entreprenør Jan Nørgaard udfører vi støbearbejde for private og erhverv, der ønsker et stabilt og gennemarbejdet grundlag til deres byggeri. Med mere end 20 års erfaring kombinerer vi faglig præcision med praktisk forståelse for jordforhold, udgravning og den videre byggeproces.
            </p>
          </div>
          <div className="shrink-0 flex flex-col sm:flex-row md:flex-col gap-3 w-full md:w-auto">
            <a
              href="tel:+4520483818"
              className="inline-flex items-center justify-center gap-2 rounded bg-brand-orange-500 px-5 py-3 font-display text-sm font-bold uppercase tracking-wider text-white hover:bg-brand-orange-600 transition-colors shadow-md text-center"
            >
              <i className="fa-solid fa-phone" /> Ring 20 48 38 18
            </a>
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center gap-2 rounded border border-white/20 bg-white/10 px-5 py-3 font-display text-sm font-bold uppercase tracking-wider text-white hover:bg-white/20 transition-colors text-center"
            >
              <i className="fa-solid fa-calculator" /> Indhent tilbud
            </Link>
          </div>
        </div>
      </div>

      {/* Sektion 1: Hvad kræver godt betonarbejde og støbearbejde? */}
      <section className="bg-white rounded-xl border border-brand-navy-100 p-6 md:p-8 shadow-xs">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-lg bg-brand-orange-500/10 text-brand-orange-500 flex items-center justify-center text-xl shrink-0">
            <i className="fa-duotone fa-circle-question" />
          </div>
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-brand-orange-600">
              Kvalitet & Forudsætninger
            </span>
            <h3 className="font-display text-2xl font-bold text-brand-navy-900">
              Hvad kræver godt betonarbejde og støbearbejde?
            </h3>
          </div>
        </div>

        <div className="text-brand-navy-700 leading-relaxed space-y-4 text-base md:text-lg">
          <p>
            Godt betonarbejde og støbearbejde handler om langt mere end at fylde beton i en form. Underlaget skal være korrekt opbygget, mål og højder skal passe, og udførelsen skal tage højde for projektets belastning. Det giver et resultat, som resten af byggeriet trygt kan føres videre på.
          </p>
          <p>
            Når opgaven gælder støbearbejde i beton, har du ofte brug for en entreprenør, der kan se hele projektet. Vi kan stå for relevante dele af udgravningen, jordarbejdet og laserafretningen, så overgangen fra rå grund til færdigt støbearbejde bliver enkel og overskuelig.
          </p>
        </div>

        {/* 4 Nøgleelementer */}
        <div className="mt-8 pt-6 border-t border-brand-navy-100">
          <h4 className="font-display text-base uppercase tracking-wider font-bold text-brand-navy-900 mb-4 flex items-center gap-2">
            <i className="fa-solid fa-shield-halved text-brand-orange-500" />
            Nøglen til et holdbart resultat:
          </h4>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {concreteRequirements.map((item, idx) => (
              <div
                key={idx}
                className="group relative rounded-lg border border-brand-navy-100 bg-brand-navy-50/50 p-4 transition-all hover:bg-white hover:shadow-md hover:border-brand-orange-500/30"
              >
                <div className="flex items-start gap-3.5">
                  <div className="h-10 w-10 shrink-0 rounded bg-brand-navy-900 text-brand-orange-400 flex items-center justify-center text-lg">
                    <i className={item.icon} />
                  </div>
                  <div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-brand-orange-600 bg-brand-orange-50 px-2 py-0.5 rounded">
                      {item.badge}
                    </span>
                    <h5 className="font-display text-base font-bold text-brand-navy-900 mt-1">
                      {item.title}
                    </h5>
                    <p className="mt-1 text-xs md:text-sm text-brand-navy-600 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 rounded-lg bg-brand-navy-50 border border-brand-navy-200/80 p-5 flex items-start gap-3.5">
            <i className="fa-solid fa-circle-info text-brand-orange-500 text-lg mt-0.5 shrink-0" />
            <p className="text-sm md:text-base text-brand-navy-800 leading-relaxed font-medium">
              Ved at vælge en entreprenør, der mestrer både udgravning, jordhåndtering, laserafretning og støbning, minimerer du risikoen for misforståelser mellem faggrupper og sparer værdifuld byggetid.
            </p>
          </div>
        </div>
      </section>

      {/* Sektion 2: Støbning af fundamenter begynder under overfladen */}
      <section className="bg-white rounded-xl border border-brand-navy-100 p-6 md:p-8 shadow-xs">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-lg bg-brand-orange-500/10 text-brand-orange-500 flex items-center justify-center text-xl shrink-0">
            <i className="fa-duotone fa-layer-group" />
          </div>
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-brand-orange-600">
              Grundighed fra bunden
            </span>
            <h3 className="font-display text-2xl font-bold text-brand-navy-900">
              Støbning af fundamenter begynder under overfladen
            </h3>
          </div>
        </div>

        <div className="text-brand-navy-700 leading-relaxed space-y-4 text-base md:text-lg">
          <p>
            Støbning af fundamenter kræver omhyggelig planlægning, fordi fejl i begyndelsen kan få betydning for de efterfølgende fag. Vi gennemgår derfor opgaven, adgangsforholdene og det ønskede resultat, før arbejdet går i gang. Så får du en løsning, der passer til byggeriet og forholdene på grunden.
          </p>
        </div>

        {/* 2 Kort: Klargøring vs Støbning */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="rounded-xl border border-brand-navy-200 bg-brand-navy-50/40 p-5 md:p-6 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2.5 mb-3 text-brand-orange-600 font-bold text-xs uppercase tracking-wider">
                <i className="fa-solid fa-compass-drafting text-base" />
                <span>Trin I: Forarbejde</span>
              </div>
              <h4 className="font-display text-lg md:text-xl font-bold text-brand-navy-900 mb-3">
                Klargøringen skaber et stabilt udgangspunkt
              </h4>
              <p className="text-brand-navy-700 text-sm md:text-base leading-relaxed">
                Et godt fundament begynder med den rigtige udgravning og en omhyggelig opbygning af underlaget. Vi arbejder med præcise niveauer og kan bruge laserafretning, når opgaven kræver det. Samtidig vurderer vi, hvordan jord og overskydende materialer håndteres mest praktisk på pladsen.
              </p>
            </div>
            <div className="mt-4 pt-3 border-t border-brand-navy-200/60 text-xs font-semibold text-brand-navy-600 flex items-center gap-2">
              <i className="fa-solid fa-check text-brand-orange-500" />
              <span>Præcis afrømning, sandpude og laserafretning</span>
            </div>
          </div>

          <div className="rounded-xl border border-brand-navy-200 bg-brand-navy-50/40 p-5 md:p-6 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2.5 mb-3 text-brand-orange-600 font-bold text-xs uppercase tracking-wider">
                <i className="fa-solid fa-trowel-bricks text-base" />
                <span>Trin II: Udførelse</span>
              </div>
              <h4 className="font-display text-lg md:text-xl font-bold text-brand-navy-900 mb-3">
                Støbningen skal passe til den videre byggeproces
              </h4>
              <p className="text-brand-navy-700 text-sm md:text-base leading-relaxed">
                Når klargøringen er på plads, udføres støbningen efter projektets mål og anvisninger. Vi holder fokus på præcision gennem hele forløbet, så den færdige konstruktion passer til den planlagte anvendelse. En god koordinering gør samtidig arbejdet lettere for dem, der skal bygge videre.
              </p>
            </div>
            <div className="mt-4 pt-3 border-t border-brand-navy-200/60 text-xs font-semibold text-brand-navy-600 flex items-center gap-2">
              <i className="fa-solid fa-check text-brand-orange-500" />
              <span>Kvalitetsbeton, armering og snorlige højder</span>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion: Systematisk Proces (Navy Dark Card - inspiration fra Kloakspuling) */}
      <section className="bg-brand-navy-900 text-white rounded-xl p-6 md:p-8 shadow-md relative overflow-hidden">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-lg bg-brand-orange-500 text-white flex items-center justify-center text-xl shrink-0">
            <i className="fa-duotone fa-arrows-to-dot" />
          </div>
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-brand-orange-400">
              Struktureret Arbejdsgang
            </span>
            <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-white">
              Vores systematiske proces for støbning af fundamenter
            </h3>
          </div>
        </div>

        <div className="text-brand-navy-200 text-base md:text-lg leading-relaxed mb-8">
          <p>
            For at sikre et fejlfrit resultat følger vi faste retningslinjer på byggepladsen, så alle detaljer er afstemt, inden betonen hældes.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {processSteps.map((step, idx) => (
            <div
              key={idx}
              className="bg-brand-navy-800/80 rounded-lg p-5 border border-brand-navy-700/80 relative flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between mb-3">
                  <span className="font-display text-3xl font-black text-brand-orange-500">
                    {step.step}
                  </span>
                  <span className="text-xs font-bold uppercase tracking-wider text-brand-orange-300 bg-brand-navy-900/80 px-2.5 py-1 rounded">
                    {step.phase}
                  </span>
                </div>
                <h5 className="font-display text-lg font-bold text-white mb-2.5 leading-snug">
                  {step.title}
                </h5>
                <p className="text-xs md:text-sm text-brand-navy-300 leading-relaxed">
                  {step.text}
                </p>
              </div>
              <div className="mt-5 pt-3 border-t border-brand-navy-700 flex items-center gap-2 text-xs text-brand-orange-300 font-semibold">
                <i className={`${step.icon} text-sm`} />
                <span>Kvalitetssikret udførelse</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Sektion 3: Fundamenter til huse skal passe til hele projektet */}
      <section className="bg-white rounded-xl border border-brand-navy-100 p-6 md:p-8 shadow-xs">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-lg bg-brand-orange-500/10 text-brand-orange-500 flex items-center justify-center text-xl shrink-0">
            <i className="fa-duotone fa-house-chimney-crack" />
          </div>
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-brand-orange-600">
              Byggeri & Bærende Konstruktioner
            </span>
            <h3 className="font-display text-2xl font-bold text-brand-navy-900">
              Fundamenter til huse skal passe til hele projektet
            </h3>
          </div>
        </div>

        <div className="text-brand-navy-700 leading-relaxed space-y-4 text-base md:text-lg">
          <p>
            Fundamenter til huse skal understøtte bygningens bærende dele og overføre belastningen sikkert til jorden. Derfor skal løsningen passe til både projektmaterialet og forholdene på grunden. Vi udfører støbning af fundamenter med blik for, at mål, koter og klargøring skal spille sammen fra start.
          </p>
          <p>
            Vi hjælper med fundamenter til nye huse og andre byggeprojekter, hvor et stabilt betonfundament er afgørende. Har projektet også behov for udgravning, dræn eller autoriseret kloakarbejde, kan vi samle flere praktiske opgaver og skabe en bedre sammenhæng på byggepladsen.
          </p>
        </div>

        {/* Saml opgaverne samlet boks */}
        <div className="mt-8 rounded-lg bg-gradient-to-r from-brand-navy-50 to-orange-50/40 border border-brand-navy-200/80 p-6">
          <h4 className="font-display text-base font-bold text-brand-navy-900 mb-2 flex items-center gap-2">
            <i className="fa-solid fa-diagram-project text-brand-orange-500" />
            Fordelen ved at samle entreprenøropgaverne ét sted:
          </h4>
          <p className="text-sm md:text-base text-brand-navy-700 leading-relaxed mb-4">
            Når du skal bygge nyt eller bygge til, hænger sokkel, udgravning, dræn og kloak tæt sammen. Som autoriseret kloakmester og erfaren entreprenør kan Jan Nørgaard varetage hele jord- og betonfasen samlet:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-white p-3.5 rounded border border-brand-navy-100 flex items-center gap-2.5 text-sm font-semibold text-brand-navy-800">
              <i className="fa-solid fa-excavator text-brand-orange-500" />
              <span>Udgravning & Sandpude</span>
            </div>
            <div className="bg-white p-3.5 rounded border border-brand-navy-100 flex items-center gap-2.5 text-sm font-semibold text-brand-navy-800">
              <i className="fa-solid fa-trowel-bricks text-brand-orange-500" />
              <span>Støbning af Fundament</span>
            </div>
            <div className="bg-white p-3.5 rounded border border-brand-navy-100 flex items-center gap-2.5 text-sm font-semibold text-brand-navy-800">
              <i className="fa-solid fa-droplet-slash text-brand-orange-500" />
              <span>Omfangsdræn & Fugt</span>
            </div>
            <div className="bg-white p-3.5 rounded border border-brand-navy-100 flex items-center gap-2.5 text-sm font-semibold text-brand-navy-800">
              <i className="fa-solid fa-pipe-valve text-brand-orange-500" />
              <span>Autoriseret Kloak</span>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 4: Hvad koster professionelt støbearbejde? */}
      <section className="bg-white rounded-xl border border-brand-navy-100 p-6 md:p-8 shadow-xs">
        <div className="max-w-3xl mb-6">
          <div className="flex items-center gap-2 mb-2">
            <div className="h-8 w-8 rounded-lg bg-brand-orange-500/10 text-brand-orange-600 flex items-center justify-center text-base">
              <i className="fa-duotone fa-calculator" />
            </div>
            <span className="text-xs font-bold uppercase tracking-wider text-brand-orange-600">
              Pris & Gennemskuelighed
            </span>
          </div>
          <h3 className="font-display text-2xl font-bold text-brand-navy-900">
            Hvad koster professionelt støbearbejde?
          </h3>
          <p className="mt-2 text-brand-navy-700 text-base md:text-lg leading-relaxed">
            Prisen på støbearbejde afhænger af mere end selve mængden af beton. Opgavens størrelse, jordforhold, udgravningsbehov, adgang for maskiner og krav til præcision har alle betydning. Derfor vurderer vi den konkrete opgave, før vi giver et tilbud, så omfanget bliver tydeligt for dig.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {priceFactors.map((factor, idx) => (
            <div
              key={idx}
              className="rounded-lg border border-brand-navy-100 bg-brand-navy-50/40 p-4 flex flex-col justify-between"
            >
              <div>
                <div className="h-9 w-9 rounded bg-brand-navy-900 text-brand-orange-400 flex items-center justify-center text-sm mb-3">
                  <i className={factor.icon} />
                </div>
                <h4 className="font-display text-base font-bold text-brand-navy-900 mb-1">
                  {factor.title}
                </h4>
                <p className="text-xs md:text-sm text-brand-navy-600 leading-relaxed">
                  {factor.desc}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-lg border-l-4 border-brand-orange-500 bg-brand-orange-50/50 p-5">
          <div className="flex items-start gap-3">
            <i className="fa-solid fa-lightbulb text-brand-orange-600 mt-1 text-lg shrink-0" />
            <div className="space-y-1">
              <h5 className="font-display text-base font-bold text-brand-navy-900">
                Få mest ud af den indledende dialog:
              </h5>
              <p className="text-sm md:text-base text-brand-navy-700 leading-relaxed">
                Du får mest ud af den indledende dialog, hvis du har tegninger, mål eller en beskrivelse af byggeriet klar. Det gør det lettere at afklare, hvilke dele vi skal håndtere, og om der er særlige forhold på grunden. Har du endnu ikke alle svar, hjælper vi med at få de praktiske spørgsmål frem.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 5: Skal vi se på dit støbearbejde i Sommersted og omegn? */}
      <section className="rounded-xl border border-brand-navy-200 bg-gradient-to-br from-white to-brand-navy-50/50 p-6 md:p-8 shadow-sm">
        <div className="flex flex-col lg:flex-row gap-8 items-start lg:items-center justify-between">
          <div className="space-y-4 max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-navy-900 text-white text-xs font-bold uppercase tracking-wider">
              <i className="fa-solid fa-location-dot text-brand-orange-400" />
              Lokal dækning i Sønderjylland
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-bold text-brand-navy-900">
              Skal vi se på dit støbearbejde i Sommersted og omegn?
            </h3>
            <p className="text-brand-navy-700 text-base md:text-lg leading-relaxed">
              Entreprenør Jan Nørgaard har base i Sommersted og løser opgaver i blandt andet Vojens, Haderslev, Kolding, Vejen og Gram. Vi hjælper både private og erhverv med støbearbejde samt tilknyttet entreprenørarbejde, og vi lægger vægt på klare aftaler, en praktisk proces og et ordentligt resultat.
            </p>
            <p className="text-brand-navy-700 text-base md:text-lg leading-relaxed">
              Kontakt os, hvis du ønsker en faglig vurdering af betonarbejde og støbearbejde til dit projekt. Fortæl gerne, hvad der skal bygges, hvor arbejdet skal udføres, og hvornår du forventer at gå i gang. Så kan vi tage en konkret snak om mulighederne og det næste skridt.
            </p>
          </div>

          <div className="w-full lg:w-72 shrink-0 bg-white p-5 rounded-lg border border-brand-navy-200 shadow-xs">
            <h4 className="font-display text-xs uppercase tracking-wider font-bold text-brand-navy-500 mb-3">
              Vi dækker blandt andet:
            </h4>
            <div className="flex flex-wrap gap-2">
              {serviceTowns.map((town, idx) => (
                <span
                  key={idx}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-bold uppercase tracking-wider ${
                    town.isBase
                      ? 'bg-brand-orange-500 text-white shadow-xs'
                      : 'bg-brand-navy-100 text-brand-navy-800'
                  }`}
                >
                  <i className={`fa-solid ${town.isBase ? 'fa-house-flag' : 'fa-location-dot text-brand-orange-600'}`} />
                  {town.name}
                  {town.isBase && <span className="text-[10px] opacity-80">(Base)</span>}
                </span>
              ))}
            </div>

            <div className="mt-5 pt-4 border-t border-brand-navy-100">
              <Link
                to="/kontakt"
                className="w-full inline-flex items-center justify-center gap-2 rounded bg-brand-navy-900 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-white hover:bg-brand-navy-800 transition-colors text-center"
              >
                Kontakt Jan Nørgaard
                <i className="fa-solid fa-arrow-right text-brand-orange-400" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 6: Afsluttende CTA Banner */}
      <section className="bg-brand-orange-500 rounded-xl p-6 md:p-8 text-white shadow-md relative overflow-hidden">
        <div className="absolute top-0 right-0 -mt-8 -mr-8 w-40 h-40 bg-white/10 rounded-full blur-2xl pointer-events-none" />
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative z-10">
          <div className="space-y-3 max-w-2xl">
            <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-white/20 text-white px-2.5 py-1 rounded">
              <i className="fa-solid fa-phone-volume" /> Uforpligtende Vurdering & Tilbud
            </span>
            <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-white">
              Få en faglig vurdering af dit støbearbejde
            </h3>
            <p className="text-white/90 text-base md:text-lg leading-relaxed">
              Fortæl os om dit byggeprojekt, hvor opgaven ligger, og hvornår du ønsker at gå i gang. Vi står klar med rådgivning, præcise priser og solidt håndværk.
            </p>
          </div>
          <div className="shrink-0 flex flex-col sm:flex-row md:flex-col gap-3 w-full md:w-auto">
            <a
              href="tel:+4520483818"
              className="inline-flex items-center justify-center gap-2 rounded bg-brand-navy-900 px-6 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-white hover:bg-brand-navy-800 transition-colors shadow-lg text-center"
            >
              <i className="fa-solid fa-phone text-brand-orange-400" /> Ring 20 48 38 18
            </a>
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center gap-2 rounded bg-white text-brand-navy-950 px-6 py-3.5 font-display text-sm font-bold uppercase tracking-wider hover:bg-brand-navy-50 transition-colors text-center shadow-md"
            >
              <i className="fa-solid fa-envelope text-brand-orange-600" /> Kontakt os i dag
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
